import { Component, OnDestroy, OnInit } from '@angular/core';
import { Cardholder, Client, DashboardData, Group } from '@app-model/dashboard';
import { FilterService } from '@app-services/filter.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-side-filter',
	templateUrl: './side-filter.component.html',
	styleUrls: ['./side-filter.component.scss']
})
export class SideFilterComponent implements OnInit, OnDestroy {
	showFiller: boolean;
	showCheckedBox: boolean;

	private setCheckedFlag(item: unknown, checked: boolean): void {
		if (!item || typeof item !== 'object') {
			return;
		}
		(item as { checked?: boolean }).checked = checked;
	}

	readonly categoryKeys = ['clients', 'groups', 'cardholders'] as const;

	searchQueries: Record<(typeof this.categoryKeys)[number], string> = {
		clients: '',
		groups: '',
		cardholders: ''
	};

	public filterOptions: DashboardData = {
		clients: [],
		groups: [],
		cardholders: []
	};

	get isFilterApplied(): boolean {
		return (
			(this.selectedIds.clients?.length || 0) > 0 ||
			(this.selectedIds.groups?.length || 0) > 0 ||
			(this.selectedIds.cardholders?.length || 0) > 0
		);
	}

	private subscription: Subscription;

	selectedIds: DashboardData;
	constructor(private readonly filterService: FilterService) {}

	ngOnInit(): void {
		// Cria uma cópia local dos selectedIds para trabalhar
		const currentSelected = this.filterService.getCurrentSelectedIds();
		this.selectedIds = this.deepClone(currentSelected);

		// Mantém a subscription para sincronizar quando outros componentes alterarem
		this.subscription = this.filterService.selectedIds$.subscribe((data: DashboardData) => {
			if (data) {
				this.selectedIds = this.deepClone(data);
				this.updateCheckedState();
			}
		});

		this.loadFilterOptions();
	}

	onCheckboxChange(
		categoryKey: (typeof this.categoryKeys)[number],
		item: Client | Group | Cardholder,
		event: MatCheckboxChange
	): void {
		this.ensureSelectedIds();

		if (event.checked) {
			this.addSelectedItem(categoryKey, item);
			this.setCheckedFlag(item, true);
		} else {
			this.removeSelectedItem(categoryKey, item);
			this.setCheckedFlag(item, false);
		}

		this.clearFilteredOutSelectedItems();
	}

	onSearch(categoryKey: (typeof this.categoryKeys)[number], query: string): void {
		this.searchQueries[categoryKey] = (query || '').trim();
	}

	public submitFilter(): void {
		this.filterService.updateSelectedIds(this.selectedIds);
		this.closeFilter();
	}

	public closeFilter(): void {
		// Restaura os valores originais do FilterService (cancela as mudanças locais)
		const originalSelected = this.filterService.getCurrentSelectedIds();
		this.selectedIds = this.deepClone(originalSelected);

		// Atualiza as propriedades checked dos itens baseado nos valores originais
		this.updateCheckedState();

		this.filterService.closeFilter();
	}

	public cleanAllCheckboxes(): void {
		this.selectedIds = {
			clients: [],
			groups: [],
			cardholders: []
		};

		// Limpa também a propriedade checked dos itens
		this.categoryKeys.forEach(category => {
			this.getFilterOptionsByCategory(category).forEach(item => {
				this.setCheckedFlag(item, false);
			});
		});
	}

	public clearCategoryCheckbox(typeCategory: (typeof this.categoryKeys)[number]): void {
		this.ensureSelectedIds();
		switch (typeCategory) {
			case 'clients':
				this.selectedIds.clients = [];
				break;
			case 'groups':
				this.selectedIds.groups = [];
				break;
			case 'cardholders':
				this.selectedIds.cardholders = [];
				break;
		}

		// Limpa também a propriedade checked dos itens da categoria
		this.getFilterOptionsByCategory(typeCategory).forEach(item => {
			this.setCheckedFlag(item, false);
		});
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	getFilteredItems(key: (typeof this.categoryKeys)[number]): Client[] | Group[] | Cardholder[] {
		const baseItems = this.getBaseFilteredItems(key);
		const q = (this.searchQueries[key] || '').toLowerCase();
		if (!q) return baseItems;

		return baseItems.filter(item => {
			const name = (item?.name || '').toString().toLowerCase();
			return name.includes(q);
		});
	}

	private getBaseFilteredItems(key: (typeof this.categoryKeys)[number]): Client[] | Group[] | Cardholder[] {
		switch (key) {
			case 'clients':
				return this.filterOptions.clients;
			case 'groups':
				return this.filteredGroups();
			case 'cardholders':
				return this.filteredCardholders();
		}
	}

	private filteredCardholders(): Cardholder[] {
		const selectedClientIds = this.selectedIds.clients?.map(c => c.id);
		const selectedGroupIds = this.selectedIds.groups?.map(g => g.id);

		return this.filterOptions.cardholders.filter(c => {
			const okClient = selectedClientIds?.length <= 0 || selectedClientIds.indexOf(c.client?.id) >= 0;
			const okGroup = selectedGroupIds?.length <= 0 || selectedGroupIds.indexOf(c.group?.id) >= 0;

			return okClient && okGroup;
		});
	}

	private filteredGroups(): Group[] {
		const selectedClientIds = this.selectedIds.clients?.map(c => c.id);

		if (selectedClientIds?.length > 0) {
			return this.filterOptions.groups.filter(g => selectedClientIds.indexOf(g.client?.id) >= 0);
		} else {
			return this.filterOptions.groups;
		}
	}

	private loadFilterOptions(): void {
		this.filterService.getDashboardFilterOptions().subscribe({
			next: (response: DashboardData) => {
				this.filterOptions = {
					clients: response?.clients || [],
					groups: response?.groups || [],
					cardholders: response?.cardholders || []
				};

				// Define o estado inicial dos checkboxes
				this.updateCheckedState();
			}
		});
	}

	private clearFilteredOutSelectedItems(): void {
		this.ensureSelectedIds();
		const selectedClientIds = this.selectedIds.clients?.map(c => c.id);

		this.selectedIds.groups = this.selectedIds.groups?.filter(g => {
			return !(selectedClientIds.length > 0 && selectedClientIds.indexOf(g.client?.id) < 0);
		});

		const selectedGroupIds = this.selectedIds.groups?.map(g => g.id);

		this.selectedIds.cardholders = this.selectedIds.cardholders?.filter(c => {
			return !(
				(selectedClientIds.length > 0 && selectedClientIds.indexOf(c.client?.id) < 0) ||
				(selectedGroupIds.length > 0 && selectedGroupIds.indexOf(c.group?.id) < 0)
			);
		});
	}

	private deepClone(obj: DashboardData): DashboardData {
		if (!obj) {
			return {
				clients: [],
				groups: [],
				cardholders: []
			};
		}
		return JSON.parse(JSON.stringify(obj));
	}

	private updateCheckedState(): void {
		this.ensureSelectedIds();
		// Primeiro, marca todos como false
		this.categoryKeys.forEach(category => {
			this.getFilterOptionsByCategory(category).forEach(item => {
				this.setCheckedFlag(item, false);
			});
		});

		// Depois, marca como true apenas os selecionados
		this.categoryKeys.forEach(category => {
			this.getSelectedByCategory(category).forEach(selectedItem => {
				const optionItem = this.getFilterOptionsByCategory(category).find(item => item.id === selectedItem.id);
				if (optionItem) {
					this.setCheckedFlag(optionItem, true);
				}
			});
		});
	}

	private ensureSelectedIds(): void {
		if (!this.selectedIds) {
			this.selectedIds = {
				clients: [],
				groups: [],
				cardholders: []
			};
		}
	}

	private addSelectedItem(categoryKey: (typeof this.categoryKeys)[number], item: Client | Group | Cardholder): void {
		switch (categoryKey) {
			case 'clients':
				this.selectedIds.clients.push(item as Client);
				return;
			case 'groups':
				this.selectedIds.groups.push(item as Group);
				return;
			case 'cardholders':
				this.selectedIds.cardholders.push(item as Cardholder);
				return;
		}
	}

	private removeSelectedItem(categoryKey: (typeof this.categoryKeys)[number], item: { id: unknown }): void {
		switch (categoryKey) {
			case 'clients': {
				const index = this.selectedIds.clients.findIndex(selectedItem => selectedItem.id === item.id);
				if (index > -1) {
					this.selectedIds.clients.splice(index, 1);
				}
				return;
			}
			case 'groups': {
				const index = this.selectedIds.groups.findIndex(selectedItem => selectedItem.id === item.id);
				if (index > -1) {
					this.selectedIds.groups.splice(index, 1);
				}
				return;
			}
			case 'cardholders': {
				const index = this.selectedIds.cardholders.findIndex(selectedItem => selectedItem.id === item.id);
				if (index > -1) {
					this.selectedIds.cardholders.splice(index, 1);
				}
				return;
			}
		}
	}

	private getFilterOptionsByCategory(category: (typeof this.categoryKeys)[number]): (Client | Group | Cardholder)[] {
		switch (category) {
			case 'clients':
				return this.filterOptions.clients;
			case 'groups':
				return this.filterOptions.groups;
			case 'cardholders':
				return this.filterOptions.cardholders;
		}
	}

	private getSelectedByCategory(category: (typeof this.categoryKeys)[number]): (Client | Group | Cardholder)[] {
		switch (category) {
			case 'clients':
				return this.selectedIds.clients;
			case 'groups':
				return this.selectedIds.groups;
			case 'cardholders':
				return this.selectedIds.cardholders;
		}
	}
}
